/* Copied from widgets/event-details/src/lib/bleed.ts so serve, Collection and care pages bleed and dress the header the way an event does. Keep the two in step. */
/**
 * HOW A WIDGET IN A COLUMN DRAWS TO THE EDGES OF THE SCREEN
 * ========================================================
 *
 * The hero is meant to run the full width of the window and to start at the
 * top of the page, while everything under it stays in the page's content
 * column. The widget does not own the page, though — on woodsidebible.org it
 * is mounted inside whatever container the WordPress template wraps it in
 * (there are eleven of them between the widget and `<body>`), and it is never
 * told how wide any of them are or how much padding they add.
 *
 * The usual trick is `width: 100vw` with a negative half-viewport margin. Two
 * things are wrong with it here. `100vw` counts the scrollbar, so on Windows
 * the hero sticks fifteen pixels past the window and the page scrolls
 * sideways; and once the element is a viewport wide, percentages inside it
 * are percentages of the viewport, so there is no longer any way to put the
 * *content* back where the column was.
 *
 * So it is measured instead. Three numbers — the gap to the left edge, the
 * gap to the right edge, and how far the hero can rise before it would run
 * into something — written to the root as custom properties. The CSS reaches
 * out by them and pads the content back in by them. No viewport units
 * anywhere, and the scrollbar never enters into it.
 */

import { useEffect, useLayoutEffect } from 'react';

/**
 * `useLayoutEffect` where there is a window, `useEffect` where there is not.
 *
 * The measurement has to happen before the browser paints or the hero shows
 * up column-width for a frame and then jumps. The dev app renders this page
 * on the server too, and React warns about `useLayoutEffect` there, so the
 * server gets the harmless one — it paints nothing.
 */
export const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface Bleed {
  /** Pixels between the widget's left edge and the page's. */
  left: number;
  /** Pixels between the widget's right edge and the page's. */
  right: number;
}

/**
 * The two gaps, never negative.
 *
 * A widget that already spans the page measures zero and the hero simply
 * stops where it is: the page gets no horizontal scrollbar out of this, which
 * is the one failure mode worth designing against.
 *
 * @param rect the widget root's rect, in viewport coordinates
 * @param bounds the page's own left and right edges — normally the window,
 *   but the body's box where the theme gives the body a narrower one, since
 *   that is the edge past which a `overflow-x: hidden` body would cut
 */
export function bleedFor(
  rect: { left: number; right: number },
  bounds: { left: number; right: number }
): Bleed {
  return {
    left: Math.max(0, Math.round(rect.left - bounds.left)),
    right: Math.max(0, Math.round(bounds.right - rect.right)),
  };
}

/**
 * Does anything between the widget and the page body clip what leaves its
 * box?
 *
 * The bleed is negative margin, and negative margin is only a promise — an
 * ancestor with `overflow: hidden` cuts the band back to the column while the
 * content stays padded inwards, which looks like a mistake rather than a
 * layout. Where that is the case the page keeps its old, honest width.
 *
 * `hidden`, `clip`, `auto` and `scroll` all cut a child off at the box;
 * `visible` is the one that does not. Named rather than inferred from
 * `!== 'visible'` so that anything unexpected — an empty string from a
 * document that has not styled itself yet, a value a future browser adds —
 * leaves the bleed alone instead of quietly turning it off.
 *
 * @param overflows computed `overflow-x`, widget outwards
 */
const CLIPS = new Set(['hidden', 'clip', 'auto', 'scroll']);

export function anyClips(overflows: readonly string[]): boolean {
  return overflows.some((value) => CLIPS.has(value));
}

/**
 * The computed `overflow-x` of everything between `el` and `<body>`,
 * stepping out of shadow roots through their hosts on the way.
 *
 * THE BODY IS DELIBERATELY NOT IN THIS LIST. Nearly every WordPress theme
 * sets `body { overflow-x: hidden }` — Woodside's does — and it is not an
 * obstacle to a full-width band, it is the thing that makes one safe: it is
 * what stops a stray wide element from giving the page a sideways scrollbar.
 * An element bled to the body's own edges is never clipped by it. Counting it
 * as a clipper was what switched the hero off on every real event page while
 * it worked perfectly in the dev app, which has no such rule.
 */
export function overflowChain(el: Element | null): string[] {
  const out: string[] = [];
  const view = el?.ownerDocument?.defaultView;
  if (!view) return out;
  const stop = el?.ownerDocument.body;
  let node: Node | null = el?.parentNode ?? null;
  while (node && node !== stop && node !== el?.ownerDocument.documentElement) {
    if (node instanceof view.ShadowRoot) node = node.host;
    else if (node instanceof view.Element) {
      out.push(view.getComputedStyle(node).overflowX);
      node = node.parentNode;
    } else node = node.parentNode;
  }
  return out;
}

/** An ancestor, as the rise cares about it. */
export interface TopBox {
  /** The outside of its border box. */
  outerTop: number;
  /** Where its own content starts: border box plus border and padding. */
  contentTop: number;
}

/**
 * How far the hero can rise before it would run into something.
 *
 * Walking outwards, an ancestor's padding is free to escape *only while the
 * widget is the first thing in it*. The moment an ancestor's content starts
 * higher than we do, there is a heading or a sibling block up there, and
 * rising any further would drive the hero over it. So the loop climbs while
 * each box's content top is exactly where we currently are, adding that box's
 * padding, and stops at the first one where it is not.
 *
 * On woodsidebible.org this walks through nine wrappers that add nothing and
 * stops at `.container-wrap`, whose 40px of top padding is the entire gap
 * between the fixed header and the page — which is exactly the gap the hero
 * is meant to close.
 *
 * @param boxes ancestors, nearest first
 * @param top the widget's own border-box top
 */
export function riseFor(boxes: readonly TopBox[], top: number): number {
  let ref = Math.round(top);
  let total = 0;
  for (const box of boxes) {
    if (Math.round(box.contentTop) !== ref) break;
    total += ref - Math.round(box.outerTop);
    ref = Math.round(box.outerTop);
  }
  return Math.max(0, total);
}

/**
 * The ancestors between `el` and `<body>`, measured for `riseFor`, stepping
 * out of shadow roots through their hosts.
 */
export function topBoxes(el: Element | null): TopBox[] {
  const out: TopBox[] = [];
  const view = el?.ownerDocument?.defaultView;
  if (!view) return out;
  const stop = el?.ownerDocument.body;
  let node: Node | null = el?.parentNode ?? null;
  while (node && node !== stop && node !== el?.ownerDocument.documentElement) {
    if (node instanceof view.ShadowRoot) node = node.host;
    else if (node instanceof view.Element) {
      const rect = node.getBoundingClientRect();
      const style = view.getComputedStyle(node);
      out.push({
        outerTop: rect.top,
        contentTop:
          rect.top + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.paddingTop) || 0),
      });
      node = node.parentNode;
    } else node = node.parentNode;
  }
  return out;
}

/**
 * HOW MUCH OF THE TOP OF THE PAGE SOMEBODY ELSE IS STANDING ON
 * ============================================================
 *
 * Once the hero runs to the top of the page and the site's header goes
 * transparent, the artwork is meant to pass *under* the header — that is the
 * whole effect. What must not pass under it is the writing: "All events" and
 * the two buttons beside it would sit behind a logo and a menu.
 *
 * So the hero keeps its content clear of whatever is fixed to the top of the
 * window, by however much that actually is. A solid header that the page
 * already makes room for measures nothing and changes nothing; a transparent
 * one measures its own height and the hero pads its content by exactly that.
 * Neither case is configured anywhere — the same measurement covers both, and
 * covers the WordPress admin bar, which is fixed to the top for staff and for
 * nobody else.
 */

/** Overlap between something fixed to the top and where the hero would start. */
export function headroomFor(fixedBottom: number, heroTop: number): number {
  return Math.max(0, Math.round(fixedBottom - heroTop));
}

/** A band fixed across the top of the window, in viewport coordinates. */
export type Band = { top: number; bottom: number };

/**
 * How far down the stack of bands fixed to the top of the window reaches.
 *
 * Chrome at the top of a page is not always one bar. A logged-in WordPress
 * editor gets the admin bar at 0, and the site's own header is then fixed
 * *below* it — so a rule that only accepts a band starting at the very top
 * finds the admin bar, stops, and reports 32px when the writing actually has
 * to clear 102. The bands are taken in order instead, each one counting only
 * while it starts at or above where the one before it ended. The first gap
 * ends the stack, which is what keeps a floating button or a sticky sidebar
 * lower down the page out of the total.
 *
 * AND THE STACK DOES NOT HAVE TO START AT ZERO. Below 783px WordPress stops
 * fixing the admin bar and lets it scroll with the page, pushing the document
 * down by its height with a margin on `<html>` instead. The bar is then not a
 * band at all, and the site's header — still fixed, still floating over the
 * artwork — is left starting at 46 with nothing above it, which a chain that
 * insists on beginning at 0 throws away. So the chain begins wherever the
 * caller says the page's own content begins, which is exactly where the hero
 * starts and exactly what the headroom is measured from. On a desktop that is
 * the bottom of the fixed admin bar and nothing changes; on a phone it is the
 * top of the page under a scrolled-away one, and the header is found.
 *
 * @param start where the page's own content begins, in viewport coordinates
 */
export function stackedBottom(bands: readonly Band[], slack = 2, start = 0): number {
  let bottom = start;
  for (const band of [...bands].sort((a, b) => a.top - b.top)) {
    if (band.top > bottom + slack) break;
    if (band.bottom > bottom) bottom = band.bottom;
  }
  return bottom;
}

/**
 * The lowest edge of anything fixed across the top of the window.
 *
 * Found by asking the document what is actually painted down the middle of
 * its top strip, rather than by looking for a selector — the widget is a
 * guest on somebody else's page and does not get to know what their header is
 * called. Anything fixed and shallow enough to be chrome counts; a fixed
 * element tall enough to be an overlay or a whole-page menu does not, or an
 * open mobile menu would push the hero down the screen.
 *
 * @param from where the page's own content begins — the stack is measured
 *   from there, and never reports less than it
 */
export function topFixedBottom(doc: Document, from = 0, strip = 240, step = 12): number {
  const view = doc.defaultView;
  if (!view || typeof doc.elementsFromPoint !== 'function') return from;
  const x = Math.round(doc.documentElement.clientWidth / 2);
  // The taller of the two, because they disagree at exactly the wrong moment.
  // `innerHeight` follows the browser's own toolbars as they slide, so a tab
  // being restored on a phone can report a window a fraction of its real
  // height for a frame or two, and a 60px header then fails this test as a
  // curtain. `clientHeight` is the layout viewport and holds still.
  const viewport = Math.max(view.innerHeight || 0, doc.documentElement.clientHeight || 0);
  const limit = viewport * 0.4;
  const seen = new Set<Element>();
  const bands: Band[] = [];
  for (let y = 1; y <= strip; y += step) {
    for (const el of doc.elementsFromPoint(x, y)) {
      if (!(el instanceof view.Element) || seen.has(el)) continue;
      seen.add(el);
      if (view.getComputedStyle(el).position !== 'fixed') continue;
      const rect = el.getBoundingClientRect();
      // Chrome, not a curtain: it has to stop well short of the fold.
      if (rect.height <= 0 || rect.height > limit) continue;
      bands.push({ top: rect.top, bottom: rect.bottom });
    }
  }
  return stackedBottom(bands, 2, from);
}
